import { DataTable } from '@customerio/pluma-components/react';
import type { Delivery } from '@/services/db/delivery/entity';
import { DeliveryTableError } from './delivery-table-error';
import { type DeliveryEmptyStateProps } from './delivery-empty-state';
import { useDeliveryTable } from './use-delivery-table';

const getRowId = (delivery: Delivery) => delivery.id;
const LOADING_STATE = { rowCount: 8 };

export type DeliveryTableProps = DeliveryEmptyStateProps & {
  deliveries: readonly Delivery[];
  isLoading?: boolean;
  isError?: boolean;
  onReload?: () => void;
  /** Continuation load-more (append semantics owned by the query). */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** Builds the detail/draft href for a row — supplied by the page. */
  getDeliveryHref: (delivery: Delivery) => string;
  /** Available channels (drives the status-legend header). */
  channels?: readonly string[];
  /** Selected channel (`''` = all). */
  actionType?: string;
  /** Recipient column header (the person taxonomy term varies by plan). */
  recipientHeader?: string;
  hasReadOnlyPermission?: boolean;
  onBulkRetry?: (ids: string[]) => void;
  onBulkSend?: (ids: string[]) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkRedeliver?: (ids: string[], options?: { tracked?: boolean }) => void;
  /** Per-row retry (expanded row) — fires directly, no confirmation, unlike bulk retry. */
  onRowRetry?: (ids: string[]) => void;
  /** Per-row delete (expanded row) — fires directly, no confirmation, unlike bulk delete. */
  onRowDelete?: (ids: string[]) => void;
};

/**
 * The Outbox deliveries table (FPS-2589). Presentational: rows + callbacks in
 * via props, no fetching. Renders the four columns, bulk selection (only
 * `isDeliverySelectable` rows, surviving load-more appends), the selected-rows
 * bulk-action bar, and continuation load-more. Row expansion + the "all"
 * (filter-wide) bulk variants are a stacked follow-up. The memoized table
 * inputs + prop defaults live in `useDeliveryTable`.
 */
export function DeliveryTable(props: DeliveryTableProps) {
  const {
    data,
    columns,
    rowSelection,
    setRowSelection,
    bulkActions,
    getRowCanSelect,
    ExpandedRowComponent,
    loadMore,
    EmptyStateComponent,
  } = useDeliveryTable(props);

  if (props.isError === true) return <DeliveryTableError onReload={props.onReload} />;

  return (
    <DataTable
      caption="Deliveries"
      data={data}
      columns={columns}
      getRowId={getRowId}
      isLoading={props.isLoading === true}
      loadingState={LOADING_STATE}
      withSorting={false}
      withRowSelection
      getRowCanSelect={getRowCanSelect}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      bulkActions={bulkActions}
      withRowExpanding
      expandedRowComponent={ExpandedRowComponent}
      loadMore={loadMore}
      emptyStateComponent={EmptyStateComponent}
    />
  );
}
